import { useState } from "react";
import Modal from "../../../../../../../components/Modal/Modal";
import ShareIcon from "../../../../../../../assets/svg/share-icon";
import CloseIcon from "../../../../../../../assets/svg/close-icon";
import NetworkIcon from "../../../../../../../assets/svg/network-icon";
import ToggleSwitch from "../../../../../../../components/ToggleSwitch/ToggleSwitch";

import LinkEmailEditorIcon from "../../../../../../../assets/svg/link-email-editor-icon";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../redux/store";

interface ShareTicketModalProps {
  ticketId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ShareTicketModal = ({
  ticketId,
  open,
  onOpenChange,
}: ShareTicketModalProps) => {
  const [email, setEmail] = useState("");
  const [isPublicLink, setIsPublicLink] = useState(false);
  const [copied, setCopied] = useState(false);
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region ?? "UK");
  const handleInvite = () => {
    if (!email?.trim()) return;
    setEmail("");
  };

  const handleCopyLink = () => {
    const link = `${window?.location?.origin}/crm/help-desk/tickets/${ticketId}`;
    navigator?.clipboard?.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      showHeader={false}
      contentClassName="bg-popper-bg-fixed-white dark:bg-popper-bg-fixed-white-dk border border-border-gray dark:border-border-gray-dk"
      bodyClassName="!p-0"
    >
      <div
        className="flex flex-col gap-9 px-6 py-[18px] w-[380px]"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <ShareIcon className="w-[18px] h-[18px] mb-0.5 shrink-0 text-verba-blue dark:text-verba-blue-dk" />
            <span className="text-base font-medium text-heading-text dark:text-heading-text-dk">
              Share this {helpdeskLabels.ticketSingular}
            </span>
          </div>
          <button
            className="p-[5px] ml-auto self-start bg-gray-100 dark:bg-tag-gray-dk rounded-full flex flex-col items-center justify-center transition-transform duration-200 hover:scale-110"
            onClick={() => onOpenChange(false)}
          >
            <CloseIcon className="w-[5.5px] h-[5.5px] text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1.5">
            <label className="text-md font-normal text-heading-text dark:text-heading-text-dk">
              Invite by name or email
            </label>
            <div className="flex items-center gap-2.5 h-9 bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg pl-2 pr-1.5 py-2">
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                placeholder="name@example.com"
                className="flex-1 text-md font-normal text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk bg-transparent outline-none min-w-0"
                onKeyDown={(e) => e?.key === "Enter" && handleInvite()}
              />
              <button
                onClick={handleInvite}
                className="flex items-center justify-center h-[26px] px-3 py-1.5 rounded-md bg-verba-indigo dark:bg-verba-indigo-dk border border-verba-indigo dark:border-verba-indigo-dk text-md font-semibold text-pure-white dark:text-pure-white-dk cursor-pointer shrink-0"
              >
                Invite
              </button>
            </div>
          </div>

          <div className="h-px bg-border-gray dark:bg-border-gray-dk" />

          <div className="flex flex-col gap-3.5">
            <div className="flex items-center justify-between h-[18px]">
              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                <NetworkIcon className="w-[18px] h-[18px] shrink-0 text-heading-text dark:text-heading-text-dk" />
                <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
                  Share link with anyone
                </span>
              </div>
              <ToggleSwitch
                isChecked={isPublicLink}
                handleToggle={() => setIsPublicLink((prev) => !prev)}
                trackerClassName="!h-[18px] !w-[34px]"
                thumbClassName="!h-[14px] !w-[14px]"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                <LinkEmailEditorIcon className="w-[18px] h-[18px] shrink-0 text-heading-text dark:text-heading-text-dk" />
                <span className="text-md font-normal text-heading-text dark:text-heading-text-dk">
                  Private link
                </span>
              </div>
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-1 h-[26px] px-2.5 py-1.5 rounded-md border border-border-gray dark:border-border-gray-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors cursor-pointer shrink-0"
              >
                <span className="text-xs font-medium text-heading-text dark:text-heading-text-dk whitespace-nowrap">
                  {copied ? "Copied!" : "Copy Link"}
                </span>
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end">
          <button
            onClick={() => onOpenChange(false)}
            className="flex items-center justify-center h-[26px] w-[100px] px-2 py-1.5 rounded-md border border-border-gray dark:border-border-gray-dk text-md font-semibold text-heading-text dark:text-heading-text-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};
